import { sleep } from '@injectivelabs/utils';
import { toHex, serializeTransaction } from 'viem';
import { EvmChainId } from '@injectivelabs/ts-types';
import { toUtf8, TxGrpcApi } from '@injectivelabs/sdk-ts';
import { Alchemy, Network as AlchemyNetwork } from 'alchemy-sdk';
import { ErrorType, WalletException, TrezorException, GeneralException, UnspecifiedErrorCode, TransactionException, } from '@injectivelabs/exceptions';
import { WalletAction, getKeyFromRpcUrl, WalletDeviceType, BaseConcreteStrategy, DEFAULT_BASE_DERIVATION_PATH, DEFAULT_ADDRESS_SEARCH_LIMIT, DEFAULT_NUM_ADDRESSES_TO_FETCH, } from '@injectivelabs/wallet-base';
import { loadTrezorConnect } from './lib.js';
import { transformTypedData } from '../utils.js';
import { BaseTrezorTransport } from './hw/index.js';
export default class TrezorBase extends BaseConcreteStrategy {
    baseDerivationPath;
    trezor;
    evmOptions;
    alchemy;
    derivationPathType;
    constructor(args) {
        super(args);
        this.evmOptions = args.evmOptions;
        this.trezor = new BaseTrezorTransport();
        this.derivationPathType = args.derivationPathType;
        this.baseDerivationPath = DEFAULT_BASE_DERIVATION_PATH;
    }
    async getWalletDeviceType() {
        return Promise.resolve(WalletDeviceType.Hardware);
    }
    async enable() {
        return Promise.resolve(true);
    }
    async disconnect() {
        return Promise.resolve();
    }
    async getAddresses() {
        const { baseDerivationPath, derivationPathType } = this;
        try {
            await this.trezor.connect();
            const accountManager = await this.trezor.getAccountManager();
            const wallets = await accountManager.getWallets(baseDerivationPath, derivationPathType);
            return wallets.map((k) => k.address);
        }
        catch (e) {
            throw new TrezorException(new Error(e.message), {
                code: UnspecifiedErrorCode,
                type: ErrorType.WalletError,
                contextModule: WalletAction.GetAccounts,
            });
        }
    }
    async getSessionOrConfirm(address) {
        return Promise.resolve(`0x${Buffer.from(`Confirmation for ${address} at time: ${Date.now()}`).toString('hex')}`);
    }
    async sendEvmTransaction(txData, args) {
        const signedTransaction = await this.signEvmTransaction(txData, args);
        try {
            const alchemy = await this.getAlchemy(args.evmChainId);
            const provider = await alchemy.config.getProvider();
            const txHash = await provider.send('eth_sendRawTransaction', [
                signedTransaction,
            ]);
            return txHash;
        }
        catch (e) {
            throw new TrezorException(new Error(e.message), {
                code: UnspecifiedErrorCode,
                type: ErrorType.WalletError,
                contextModule: WalletAction.SendEvmTransaction,
            });
        }
    }
    async sendTransaction(transaction, options) {
        const { endpoints, txTimeout } = options;
        if (!endpoints) {
            throw new WalletException(new Error('You have to pass endpoints.grpc within the options for using Ethereum native wallets'));
        }
        const txApi = new TxGrpcApi(endpoints.grpc);
        const response = await txApi.broadcast(transaction, { txTimeout });
        if (response.code !== 0) {
            throw new TransactionException(new Error(response.rawLog), {
                code: UnspecifiedErrorCode,
                contextCode: response.code,
                contextModule: response.codespace,
            });
        }
        return response;
    }
    async signEip712TypedData(eip712json, address) {
        const TrezorConnect = await loadTrezorConnect();
        const object = JSON.parse(eip712json);
        const compatibleObject = {
            ...object,
            domain: {
                ...object.domain,
                chainId: object.domain.chainId,
                salt: '0',
            },
        };
        const dataWithHashes = transformTypedData(compatibleObject);
        const { types: { EIP712Domain = [], ...otherTypes } = {}, message = {}, domain = {}, primaryType, domain_separator_hash, message_hash, } = dataWithHashes;
        try {
            await this.trezor.connect();
            const { derivationPath } = await this.getWalletForAddress(address);
            const response = await TrezorConnect.ethereumSignTypedData({
                path: derivationPath,
                data: {
                    types: { EIP712Domain, ...otherTypes },
                    message,
                    domain,
                    primaryType,
                },
                message_hash,
                domain_separator_hash,
                metamask_v4_compat: true,
            });
            if (!response.success) {
                // noinspection ExceptionCaughtLocallyJS
                throw new Error((response.payload && response.payload.error) || 'Unknown error');
            }
            return response.payload.signature;
        }
        catch (e) {
            throw new TrezorException(new Error(e.message), {
                code: UnspecifiedErrorCode,
                type: ErrorType.WalletError,
                contextModule: WalletAction.SignTransaction,
            });
        }
    }
    async signAminoCosmosTransaction(_transaction) {
        throw new WalletException(new Error('This wallet does not support signing Cosmos transactions'), {
            code: UnspecifiedErrorCode,
            type: ErrorType.WalletError,
            contextModule: WalletAction.SendTransaction,
        });
    }
    async signCosmosTransaction(_transaction) {
        throw new WalletException(new Error('This wallet does not support signing Cosmos transactions'), {
            code: UnspecifiedErrorCode,
            type: ErrorType.WalletError,
            contextModule: WalletAction.SendTransaction,
        });
    }
    async signArbitrary(signer, data) {
        const TrezorConnect = await loadTrezorConnect();
        try {
            await this.trezor.connect();
            const { derivationPath } = await this.getWalletForAddress(signer);
            const response = await TrezorConnect.ethereumSignMessage({
                path: derivationPath,
                message: toUtf8(data),
            });
            if (!response.success) {
                throw new Error((response.payload && response.payload.error) || 'Unknown error');
            }
            return response.payload.signature;
        }
        catch (e) {
            throw new TrezorException(new Error(e.message), {
                code: UnspecifiedErrorCode,
                type: ErrorType.WalletError,
                contextModule: WalletAction.SignTransaction,
            });
        }
    }
    async getEthereumChainId() {
        const alchemy = await this.getAlchemy();
        const alchemyProvider = await alchemy.config.getProvider();
        return alchemyProvider.network.chainId.toString();
    }
    async getEvmTransactionReceipt(txHash, evmChainId) {
        const alchemy = await this.getAlchemy(evmChainId);
        const provider = await alchemy.config.getProvider();
        const interval = 3000;
        const maxAttempts = 10;
        let attempts = 0;
        while (attempts < maxAttempts) {
            attempts++;
            await sleep(interval);
            try {
                const receipt = await provider.send('eth_getTransactionReceipt', [
                    txHash,
                ]);
                if (receipt) {
                    return txHash;
                }
            }
            catch { }
        }
        throw new Error(`Failed to retrieve transaction receipt for txHash: ${txHash}`);
    }
    async getPubKey() {
        throw new WalletException(new Error('You can only fetch PubKey from Cosmos native wallets'));
    }
    async signEvmTransaction(txData, args) {
        const TrezorConnect = await loadTrezorConnect();
        const chainId = parseInt(args.evmChainId.toString(), 10);
        const alchemy = await this.getAlchemy(args.evmChainId);
        const nonce = await alchemy.core.getTransactionCount(args.address);
        // Handle hex string values properly (with or without 0x prefix)
        const parseHexValue = (value) => {
            if (typeof value === 'string') {
                const hexValue = value.startsWith('0x') ? value : `0x${value}`;
                return BigInt(hexValue);
            }
            return BigInt(value);
        };
        // Convert to BigInt first, then to hex for Trezor
        const valueBigInt = parseHexValue(txData.value || '0x0');
        const gasBigInt = parseHexValue(txData.gas);
        const maxFeePerGasBigInt = parseHexValue(txData.maxFeePerGas);
        const maxPriorityFeePerGasBigInt = parseHexValue(txData.maxPriorityFeePerGas);
        // Create transaction data for Trezor API (needs hex strings)
        const trezorTxData = {
            to: txData.to,
            value: toHex(valueBigInt),
            gasLimit: toHex(gasBigInt),
            nonce: toHex(nonce),
            data: txData.data || '0x',
            chainId,
            maxFeePerGas: toHex(maxFeePerGasBigInt),
            maxPriorityFeePerGas: toHex(maxPriorityFeePerGasBigInt),
        };
        try {
            await this.trezor.connect();
            const { derivationPath } = await this.getWalletForAddress(args.address);
            const response = await TrezorConnect.ethereumSignTransaction({
                path: derivationPath,
                transaction: trezorTxData,
            });
            if (!response.success) {
                throw new TrezorException(new Error((response.payload && response.payload.error) ||
                    'Something happened while signing with Trezor'), {
                    code: UnspecifiedErrorCode,
                    type: ErrorType.WalletError,
                    contextModule: WalletAction.SignEvmTransaction,
                });
            }
            // Create viem-compatible transaction data for serialization
            const viemTxData = {
                type: 'eip1559',
                chainId,
                nonce,
                to: txData.to,
                value: valueBigInt,
                data: (txData.data || '0x'),
                gas: gasBigInt,
                maxFeePerGas: maxFeePerGasBigInt,
                maxPriorityFeePerGas: maxPriorityFeePerGasBigInt,
                v: BigInt(response.payload.v),
                r: response.payload.r,
                s: response.payload.s,
            };
            return serializeTransaction(viemTxData);
        }
        catch (e) {
            if (e instanceof TrezorException) {
                throw e;
            }
            throw new TrezorException(new Error(e.message), {
                code: UnspecifiedErrorCode,
                type: ErrorType.WalletError,
                contextModule: WalletAction.SignEvmTransaction,
            });
        }
    }
    async getWalletForAddress(address) {
        const { baseDerivationPath, derivationPathType } = this;
        const accountManager = await this.trezor.getAccountManager();
        if (!accountManager.hasWalletForAddress(address)) {
            for (let i = 0; i < DEFAULT_ADDRESS_SEARCH_LIMIT / DEFAULT_NUM_ADDRESSES_TO_FETCH; i += 1) {
                await accountManager.getWallets(baseDerivationPath, derivationPathType);
                if (accountManager.hasWalletForAddress(address)) {
                    return (await accountManager.getWalletForAddress(address));
                }
            }
        }
        return (await accountManager.getWalletForAddress(address));
    }
    async getAlchemy(evmChainId) {
        if (this.alchemy) {
            return this.alchemy;
        }
        const options = this.evmOptions;
        const chainId = evmChainId || options.evmChainId;
        const url = options.rpcUrl || options.rpcUrls?.[chainId];
        if (!url) {
            throw new GeneralException(new Error('Please pass rpcUrl within the ethereumOptions'));
        }
        this.alchemy = new Alchemy({
            apiKey: getKeyFromRpcUrl(url),
            network: chainId === EvmChainId.Mainnet
                ? AlchemyNetwork.ETH_MAINNET
                : AlchemyNetwork.ETH_SEPOLIA,
        });
        return this.alchemy;
    }
}
